import { useState, useEffect, useRef } from 'react'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import OnlinePaymentService from 'services/OnlinePayment'
import CopyButton from 'components/CopyButton'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { DateTime } from 'luxon'
import * as FA from 'react-icons/fa'

import {
  SubContainer,
  Row,
  Column,
  Button,
  MaskedInput,
  FormField,
  QRCodeImage,
  PixCode,
  StatusBadge,
  InfoText,
  SubTitle,
  ErrorContainer
} from '../../styles'

/*
  De quanto em quanto tempo perguntamos à API se o Pix caiu. O Asaas avisa a `cutilei-api`
  por webhook, então o que este intervalo atrasa é só a tela — cinco segundos é curto o
  bastante para parecer instantâneo e longo o bastante para não martelar a API enquanto o
  cliente procura o aplicativo do banco.
*/
const POLL_INTERVAL = 5000

export default function PixCharge ({
  businessId,
  customerId,
  intended = [],
  value,
  onPaid: handlePaid = () => null,
  onCharged: handleCharged = () => null,
  onExpired: handleExpired = () => null
}) {
  const [loading, setLoading] = useState (true)
  const [charge, setCharge] = useState (null)
  const [errorMessage, setErrorMessage] = useState (null)

  /*
    O CPF só é pedido quando a API diz que precisa.

    O Asaas não emite cobrança para um cliente sem documento, e o link de agendamento nunca
    pediu CPF — mas quem já pagou uma vez tem o cadastro lá, e perguntar de novo seria
    atrito à toa. Por isso a primeira tentativa vai sem: se voltar `cpf_required`, o campo
    aparece e a cobrança é refeita com ele.
  */
  const [cpf, setCpf] = useState ('')
  const [cpfRequired, setCpfRequired] = useState (false)

  /*
    A chave nasce fora do efeito e vive num ref porque o `StrictMode` monta o componente
    duas vezes em desenvolvimento: as duas chamadas saem com a mesma chave e a API devolve
    a mesma cobrança, em vez de dois QRs para o mesmo agendamento.
  */
  const idempotencyKey = useRef (OnlinePaymentService.newIdempotencyKey ())

  /* Quanto falta do prazo da reserva, em milissegundos. `null` quando não há o que contar. */
  const [remaining, setRemaining] = useState (null)

  useEffect (() => {
    createCharge ()
  }, [])

  /*
    A contagem regressiva do horário reservado.

    O prazo que importa aqui é o da reserva, e não o do QR Code: o código do Asaas costuma
    valer meses, e o horário, quinze minutos. Quando ele zera o horário volta para a praça, e
    continuar mostrando um QR pagável seria convidar o cliente a pagar o que não é mais dele.
  */
  useEffect (() => {
    if (!charge?.holdExpiresAt || charge.status !== 'pending') return setRemaining (null)

    const deadline = DateTime.fromISO (charge.holdExpiresAt)

    const tick = () => {
      const left = deadline.diffNow ().toMillis ()

      setRemaining (left)

      if (left <= 0) handleExpired ()
    }

    tick ()
    const interval = setInterval (tick, 1000)

    return () => clearInterval (interval)
  }, [charge?.holdExpiresAt, charge?.status])

  /*
    Só perguntamos enquanto houver o que esperar. Cobrança confirmada, expirada ou recusada
    não muda mais sozinha, e deixar o intervalo vivo nesses casos seria bater na API para
    sempre com a tela aberta.

    As dependências são o id e o status, e não `charge`: cada consulta devolve um objeto
    novo, então depender dele derrubaria e recriaria o intervalo a cada volta, cinco
    segundos depois da anterior — o que na prática é o mesmo que nunca mais consultar.
  */
  useEffect (() => {
    if (!charge || !OnlinePaymentService.isPending (charge.status)) return

    const interval = setInterval (getStatus, POLL_INTERVAL)

    return () => clearInterval (interval)
  }, [charge?.id, charge?.status])

  const createCharge = async () => {
    setLoading (true)
    setErrorMessage (null)

    try {
      /*
        A cobrança nasce junto da reserva do horário: é esta chamada que segura o horário do
        cliente enquanto ele paga. Por isso ela é ancorada no salão, e não num agendamento —
        que ainda não existe.
      */
      const { data } = await cutileiApi.post (
        `/businesses/${businessId}/deposit/pix`,
        {
          customerId,
          schedules: intended.map (item => ({
            date: item.date,
            time: item.time,
            serviceId: item.serviceId,
            professionalId: item.professionalId
          })),
          ...cpf && {cpfCnpj: cpf.replace (/\D/g, '')}
        },
        { anonymous: true, headers: {'X-Idempotency-Key': idempotencyKey.current} }
      )

      setCharge (data)
      handleCharged (data)
      setCpfRequired (false)
    } catch (error) {
      logApiError (error)

      /*
        Pedir o documento não é falha: é o próximo passo. A mensagem some para não parecer
        erro, e o campo aparece no lugar do QR.
      */
      if (error.response?.data?.code === 'cpf_required') {
        setCpfRequired (true)
        setErrorMessage (null)
      } else {
        setErrorMessage (getApiErrorMessage (error))
      }

      /*
        Falha sem resposta pode ter criado a cobrança do outro lado, então a repetição vai
        com a mesma chave. Recusa com resposta não criou nada, e insistir na chave antiga
        devolveria a mesma recusa para sempre.
      */
      if (!OnlinePaymentService.shouldKeepIdempotencyKey (error)) {
        idempotencyKey.current = OnlinePaymentService.newIdempotencyKey ()
      }
    } finally {
      setLoading (false)
    }
  }

  const getStatus = async () => {
    try {
      const { data } = await cutileiApi.get (`/deposits/${charge.id}`, { anonymous: true })

      setCharge (data)
      handleCharged (data)

      /*
        Os agendamentos vêm na resposta da confirmação: é o pagamento que os cria, e esta é a
        primeira vez que eles existem. Sem eles a tela de sucesso não teria o que mostrar.
      */
      if (data.status === 'confirmed') handlePaid (data, data.schedules ?? [])
    } catch (error) {
      /*
        Falhar em uma consulta não é motivo para trocar a tela por um erro: o QR na mão do
        cliente continua válido, e a próxima volta do intervalo tenta de novo.
      */
      logApiError (error)
    }
  }

  const generateNewCode = () => {
    idempotencyKey.current = OnlinePaymentService.newIdempotencyKey ()
    setCharge (null)
    createCharge ()
  }

  if (loading) return <Loading style={{marginTop: 20}}/>

  /*
    O documento vai para a cobrança e para o cadastro do cliente no Asaas, e não é guardado
    deste lado — do segundo sinal em diante o vínculo já existe e este passo não aparece.
  */
  if (cpfRequired && !charge) return (
    <SubContainer style={{gap: 12}}>
      <InfoText>
        Para gerar o Pix, informe o CPF de quem vai pagar. Pedimos só desta vez.
      </InfoText>
      <FormField>
        <MaskedInput
          name='cpf'
          placeholder='CPF'
          options={{blocks: [3, 3, 3, 2], delimiters: ['.', '.', '-'], numericOnly: true}}
          value={cpf}
          onChange={event => setCpf (event.target.value)}
        />
      </FormField>
      {errorMessage && (
        <ErrorContainer>
          <InfoText style={{color: '#FFFFFF'}}>{errorMessage}</InfoText>
        </ErrorContainer>
      )}
      <Button
        onClick={createCharge}
        disabled={cpf.replace (/\D/g, '').length !== 11}
      >
        Gerar código Pix
      </Button>
    </SubContainer>
  )

  if (!charge) return (
    <SubContainer style={{gap: 12}}>
      {errorMessage && (
        <ErrorContainer>
          <InfoText style={{color: '#FFFFFF'}}>{errorMessage}</InfoText>
        </ErrorContainer>
      )}
      <Button onClick={createCharge}>Tentar novamente</Button>
    </SubContainer>
  )

  if (charge.status === 'confirmed') return (
    <SubContainer style={{marginTop: 15, gap: 8}}>
      <FA.FaCheckCircle size={34} color='#44CF6C'/>
      <SubTitle style={{color: '#44CF6C'}}>Pagamento confirmado!</SubTitle>
    </SubContainer>
  )

  if (charge.status === 'expired') return (
    <SubContainer style={{marginTop: 15, gap: 12}}>
      <InfoText>
        Este código Pix expirou. Gere um novo para adiantar o pagamento.
      </InfoText>
      <Button onClick={generateNewCode}>Gerar novo código</Button>
    </SubContainer>
  )

  const countdown = remaining > 0
    ? [Math.floor (remaining / 60000), Math.floor ((remaining % 60000) / 1000)]
      .map (part => String (part).padStart (2, '0')).join (':')
    : null

  return (
    <SubContainer style={{gap: 12}}>
      <Row style={{justifyContent: 'center'}}>
        <StatusBadge color={OnlinePaymentService.getStatusColor (charge.status)}>
          {OnlinePaymentService.getStatusLabel (charge.status)}
        </StatusBadge>
      </Row>
      <InfoText>
        Abra o aplicativo do seu banco, escolha pagar com Pix e escaneie o código abaixo.
      </InfoText>
      {charge.pix?.encodedImage && (
        <QRCodeImage
          src={`data:image/png;base64,${charge.pix.encodedImage}`}
          alt='QR Code do Pix'
        />
      )}
      {charge.pix?.payload && (
        <Column style={{width: '100%', padding: 0, gap: 8}}>
          <InfoText style={{color: '#666666'}}>Ou use o Pix copia e cola:</InfoText>
          <PixCode>{charge.pix.payload}</PixCode>
          <CopyButton text={charge.pix.payload} label='Copiar código'/>
        </Column>
      )}
      {countdown && (
        <Row style={{justifyContent: 'center', gap: 6}}>
          <FA.FaRegClock size={13} color='#999999'/>
          <InfoText style={{color: '#999999', fontSize: 13}}>
            {`Seu horário está reservado por mais ${countdown}`}
          </InfoText>
        </Row>
      )}
      <InfoText style={{color: '#999999', fontSize: 13}}>
        Assim que o pagamento cair, esta tela avança sozinha.
      </InfoText>
    </SubContainer>
  )
}
